import { Text, Box, Center, Row, useTheme, Pressable, IToastProps } from 'native-base'
import React from 'react'
import { StyleSheet, useWindowDimensions } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { useDeviceInfo } from '../useDeviceInfo'

import { Icon } from '~components/Icon'
import { BoxWithShadow } from '~components/atoms/BoxWithShadow'
import { IconNames } from '~types/icon'
import { ColorNames } from '~types/native-base'
import { getColorValue } from '~utils'

export type ToastType = Pick<IToastProps, 'description' | 'placement'> & {
  onPress: () => void
  variant: 'danger' | 'warning' | 'success' | 'info'
}

type ToastPropertiesType = {
  bgColor: ColorNames
  icon: IconNames
  secondColor: ColorNames
}
type ToastVariantsType = {
  [variant in ToastType['variant']]: ToastPropertiesType
}
const getCorrectProperties: ToastVariantsType = {
  danger: {
    bgColor: 'danger100',
    icon: 'close-circle-line',
    secondColor: 'danger800',
  },
  warning: {
    bgColor: 'warning02',
    icon: 'alert-line',
    secondColor: 'warning01',
  },
  success: {
    bgColor: 'Success01',
    icon: 'checkbox-circle-line',
    secondColor: 'tertiary600',
  },
  info: {
    bgColor: 'info100',
    icon: 'information-line',
    secondColor: 'info600',
  },
}
export const Toast = ({ description, variant = 'info', onPress }: ToastType) => {
  const { breakpoints, colors } = useTheme()
  const { width: windowWidth } = useWindowDimensions()

  const progress = useSharedValue(1)
  const { deviceSpecificWidth, isWeb } = useDeviceInfo()
  const width =
    isWeb && deviceSpecificWidth >= breakpoints.desktop ? deviceSpecificWidth : windowWidth - 32
  const rStyle = useAnimatedStyle(() => {
    return { width: width * progress.value }
  })
  progress.value = withTiming(0, { duration: 5000 })
  const { bgColor, secondColor, icon } = getCorrectProperties[variant]

  return (
    <Pressable onPress={onPress}>
      <BoxWithShadow shadowSize="t" mb="30" rounded="lg">
        <Box overflow="hidden" width={width} rounded="lg">
          <Row flex={1}>
            <Box bg={bgColor} justifyContent="center" p="4">
              <Icon size={20} name={icon} color={secondColor} />
            </Box>
            <Box justifyContent="center" p="4" flex={1}>
              <Text>{description}</Text>
            </Box>
            <Center px="5">
              <Icon name="close-line" size={16} color="offMain" />
            </Center>
          </Row>
          <Box>
            <Animated.View
              style={[
                styles.animationBar,
                rStyle,
                { backgroundColor: getColorValue({ color: secondColor, colors }) },
              ]}
            />
          </Box>
        </Box>
      </BoxWithShadow>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  animationBar: {
    height: 2,
  },
})
